import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Stack } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { ResourceDeployResponse } from '../../../api/resources';
import {
	DATE_COMPLIANCE_EMPTY_ZIP_MESSAGE,
	DATE_COMPLIANCE_FAILURE_MESSAGE,
	DATE_COMPLIANCE_SUCCESS_MESSAGE,
	MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
	MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
} from '../../../data/constants';

interface DeployResponseSummaryProps {
	response: ResourceDeployResponse[];
}

const DeployResponseSummary = (props: DeployResponseSummaryProps) => {
	const { response } = props;

	const successfulResources = response.filter((r) => r.success === true);
	const failedResources = response.filter((r) => r.success === false);

	return (
		<Stack spacing={2}>
			{successfulResources.length === 0 && failedResources.length === 0 && (
				<Alert
					icon={false}
					severity='info'
					sx={{
						alignItems: 'flex-start',
						width: '100%',
						'&.MuiAlert-message': {
							width: '100%',
						},
					}}
				>
					<Typography>{DATE_COMPLIANCE_EMPTY_ZIP_MESSAGE()}</Typography>
				</Alert>
			)}
			{successfulResources.length > 0 && (
				<Alert
					icon={false}
					security='success'
					sx={{
						alignItems: 'flex-start',
						width: '100%',
						'&.MuiAlert-message': {
							width: '100%',
						},
					}}
				>
					<Accordion
						disableGutters
						sx={{
							backgroundColor: 'transparent',
							border: 'none',
							boxShadow: 'none',
							width: MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls='success-tables-list'
						>
							<Stack spacing={2} direction='row'>
								<CheckCircleIcon color='inherit' />
								<Typography>
									{successfulResources.length +
										' ' +
										DATE_COMPLIANCE_SUCCESS_MESSAGE(
											successfulResources.length > 1,
										)}
								</Typography>
							</Stack>
						</AccordionSummary>
						<AccordionDetails>
							<Box
								sx={{
									maxHeight: MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
									overflowX: 'hidden',
								}}
							>
								<Stack spacing={1}>
									{successfulResources.map((resource) => (
										<Typography key={resource.name} variant='caption'>
											{resource.name}
										</Typography>
									))}
								</Stack>
							</Box>
						</AccordionDetails>
					</Accordion>
				</Alert>
			)}
			{failedResources.length > 0 && (
				<Alert
					icon={false}
					severity='error'
					sx={{
						alignItems: 'flex-start',
						width: '100%',
						'&.MuiAlert-message': {
							width: '100%',
						},
					}}
				>
					<Accordion
						disableGutters
						sx={{
							backgroundColor: 'transparent',
							border: 'none',
							boxShadow: 'none',
							width: MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls='failed-tables-list'
						>
							<Stack spacing={2} direction='row'>
								<ErrorIcon color='inherit' />
								<Typography>
									{failedResources.length +
										' ' +
										DATE_COMPLIANCE_FAILURE_MESSAGE(failedResources.length > 1)}
								</Typography>
							</Stack>
						</AccordionSummary>
						<AccordionDetails>
							<Box
								sx={{
									maxHeight: MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
									overflowY: 'scroll',
									overflowX: 'hidden',
								}}
							>
								<Stack spacing={1}>
									{failedResources.map((resource) => (
										<Typography key={resource.name} variant='caption'>
											{resource.error}
										</Typography>
									))}
								</Stack>
							</Box>
						</AccordionDetails>
					</Accordion>
				</Alert>
			)}
		</Stack>
	);
};

export default DeployResponseSummary;
